// src/pages/admin/ElectionResultsAdminPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL_ELECTIONS } from '../../../utils/constants';
import ElectionProtectedRoute from '../../../components/auth/ElectionProtectedRoute';

const ElectionResultsAdminPage = () => {
  const { electionId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    fetchResults();
    
    // Auto-refresh every 5 seconds
    const interval = setInterval(() => {
      fetchResults();
    }, 5000);

    return () => clearInterval(interval);
  }, [electionId]);

  const fetchResults = async () => {
    try {
      const response = await fetch(`${API_BASE_URL_ELECTIONS}/elections/${electionId}/results`);
      const data = await response.json();
      if (data.success) {
        setResults(data.data);
        setLastUpdate(new Date());
      }
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportResults = () => {
    if (!results) return;
    
    const csv = generateCSV(results);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${results.election.title.replace(/\s+/g, '_')}_results.csv`;
    a.click();
  };

  const generateCSV = (results) => {
    let csv = 'Position,Candidate,Votes,Percentage\n';
    
    results.results.forEach(position => {
      position.candidates.forEach(candidate => {
        csv += `"${position.position.title}","${candidate.fullName}",${candidate.voteCount},${candidate.percentage}%\n`;
      });
    });
    
    return csv;
  };

  const getTotalVotes = () => {
    if (!results) return 0;
    return results.results.reduce((sum, position) => sum + position.totalVotes, 0);
  };

  const getTotalVoters = () => {
    if (!results || results.results.length === 0) return 0;
    // Use the position with most votes as reference (since each student votes once)
    return Math.max(...results.results.map(r => r.totalVotes));
  };

  if (loading) {
    return (
      <ElectionProtectedRoute>
        <div className="flex items-center justify-center min-h-screen bg-black">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mx-auto mb-4"></div>
            <p className="text-gray-400 font-semibold">Loading Results...</p>
          </div>
        </div>
      </ElectionProtectedRoute>
    );
  }

  return (
    <ElectionProtectedRoute>
      <div className="min-h-screen bg-black">
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-900 via-black to-gray-900 border-b border-red-900/30 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <button
                  onClick={() => navigate('/admin/manage-elections')}
                  className="flex items-center gap-2 text-red-400 hover:text-red-300 mb-3 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Elections
                </button>
                <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-3">
                  <span className="text-red-500">📊</span>
                  {results?.election?.title}
                </h1>
                <p className="text-gray-400">{results?.election?.school?.name}</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={exportResults}
                  className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-lg font-semibold shadow-lg shadow-green-600/30 flex items-center gap-2 transition-all duration-300 transform hover:scale-105"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </button>
              </div>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300"></div>
                <div className="relative bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-gray-400 text-sm font-medium mb-2">Total Votes Cast</div>
                      <div className="text-5xl font-black text-red-500">{getTotalVotes()}</div>
                    </div>
                    <div className="p-4 bg-red-900/30 rounded-lg border border-red-500/30">
                      <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300"></div>
                <div className="relative bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-gray-400 text-sm font-medium mb-2">Total Positions</div>
                      <div className="text-5xl font-black text-red-500">{results?.results?.length || 0}</div>
                    </div>
                    <div className="p-4 bg-red-900/30 rounded-lg border border-red-500/30">
                      <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative group">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-green-600 to-green-700 rounded-xl opacity-20 group-hover:opacity-40 blur transition duration-300"></div>
                <div className="relative bg-gradient-to-br from-gray-900 to-black border border-green-900/30 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-gray-400 text-sm font-medium mb-2">Last Updated</div>
                      <div className="text-xl font-bold text-white flex items-center gap-2">
                        <span className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></span>
                        {lastUpdate.toLocaleTimeString()}
                      </div>
                    </div>
                    <div className="p-4 bg-green-900/30 rounded-lg border border-green-500/30">
                      <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-8">
            {results?.results?.map((position, posIndex) => {
              const winner = position.candidates[0];
              
              return (
                <div key={position.position.id} className="group relative">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl opacity-0 group-hover:opacity-20 blur transition duration-300"></div>
                  <div className="relative bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl shadow-2xl overflow-hidden">
                    {/* Position Header */}
                    <div className="bg-gradient-to-r from-gray-800 via-gray-900 to-black p-6 border-b border-red-900/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
                            <span className="text-red-500">{posIndex + 1}.</span>
                            {position.position.title}
                          </h2>
                          {position.position.description && (
                            <p className="text-gray-400">{position.position.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-4xl font-black text-red-500">{position.totalVotes}</div>
                          <div className="text-gray-400 text-sm">Total Votes</div>
                        </div>
                      </div>
                    </div>

                    {/* Candidates Results */}
                    <div className="p-6">
                      <div className="space-y-6">
                        {position.candidates.map((candidate, candIndex) => {
                          const isWinner = candIndex === 0 && candidate.voteCount > 0;
                          
                          return (
                            <div
                              key={candidate.id}
                              className={`relative rounded-xl p-6 transition-all border-2 ${
                                isWinner
                                  ? 'bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-yellow-500/50 shadow-lg shadow-yellow-500/20'
                                  : 'bg-gray-800/50 border-gray-700'
                              }`}
                            >
                              {/* Winner Badge */}
                              {isWinner && (
                                <div className="absolute -top-3 -right-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 px-4 py-2 rounded-full font-black text-sm shadow-lg animate-bounce flex items-center gap-1">
                                  <span>👑</span>
                                  <span>WINNER</span>
                                </div>
                              )}

                              <div className="flex items-center gap-6">
                                {/* Rank */}
                                <div className={`text-center ${
                                  isWinner ? 'w-16 h-16' : 'w-12 h-12'
                                } ${
                                  isWinner
                                    ? 'bg-gradient-to-br from-yellow-400 to-orange-500'
                                    : candIndex === 1
                                    ? 'bg-gradient-to-br from-gray-400 to-gray-500'
                                    : candIndex === 2
                                    ? 'bg-gradient-to-br from-orange-400 to-orange-500'
                                    : 'bg-gray-600'
                                } rounded-full flex items-center justify-center text-white font-black ${
                                  isWinner ? 'text-2xl' : 'text-lg'
                                } shadow-lg`}>
                                  {candIndex + 1}
                                </div>

                                {/* Photo */}
                                {candidate.photo && (
                                  <img
                                    src={candidate.photo}
                                    alt={candidate.fullName}
                                    className={`${
                                      isWinner ? 'w-24 h-24' : 'w-16 h-16'
                                    } rounded-full object-cover border-4 ${
                                      isWinner ? 'border-yellow-400' : 'border-gray-600'
                                    } shadow-lg`}
                                  />
                                )}

                                {/* Candidate Info */}
                                <div className="flex-1">
                                  <h3 className={`font-bold ${
                                    isWinner ? 'text-2xl text-yellow-400' : 'text-xl text-white'
                                  }`}>
                                    {candidate.fullName}
                                  </h3>
                                  {candidate.bio && (
                                    <p className="text-sm text-gray-400 mt-1">{candidate.bio}</p>
                                  )}

                                  {/* Progress Bar */}
                                  <div className="mt-4">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-sm font-semibold text-gray-300">
                                        {candidate.voteCount} votes
                                      </span>
                                      <span className={`text-2xl font-black ${
                                        isWinner ? 'text-yellow-400' : 'text-red-500'
                                      }`}>
                                        {candidate.percentage}%
                                      </span>
                                    </div>
                                    <div className="h-4 bg-gray-700 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all duration-1000 ${
                                          isWinner
                                            ? 'bg-gradient-to-r from-yellow-400 to-orange-500'
                                            : 'bg-gradient-to-r from-red-600 to-red-700'
                                        }`}
                                        style={{ width: `${candidate.percentage}%` }}
                                      ></div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* No Results */}
          {(!results?.results || results.results.length === 0) && (
            <div className="bg-gradient-to-br from-gray-900 to-black border border-red-900/30 rounded-2xl shadow-xl p-20">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-24 h-24 bg-red-900/20 rounded-full mb-6 border border-red-500/30">
                  <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">No Results Yet</h3>
                <p className="text-gray-400">Votes will appear here once voting begins</p>
              </div>
            </div>
          )}

          {/* Auto-refresh Notice */}
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-900 to-black border border-green-500/30 rounded-full text-white text-sm shadow-lg">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span className="text-gray-300">Auto-updating every 5 seconds</span>
            </div>
          </div>
        </div>
      </div>
    </ElectionProtectedRoute>
  );
};

export default ElectionResultsAdminPage;